import '@forest/native-diagnostics'

import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  initializeNativeBridge,
  native,
  pageVisibleOnce,
  type Product,
  type Products,
} from '@forest/native-services'
import {
  failureOf,
  tagFailure,
  withFailure,
  type PageLoadStep,
} from '@forest/paywall-core'
import { dialog } from '@forest/shared/react'
import { compare } from 'compare-versions'
import { fetchConfig } from './config'
import { mockingReady } from './lib/enable-mocking'
import { getChinaAndroidTrialEligibility } from './lib/get-china-android-trial-eligibility'
import './lib/posthog'
import { loadingBifrost } from './loadings'
import { m } from './paraglide/messages'
import { defineCustomClientStrategy } from './paraglide/runtime'
import { appVersion, language, platform } from './params'
import { api } from './services/api'
import { $chinaAndroidTrialEligibility, setProducts } from './store'
import { $config } from './store/config'
import { loadingOverlay } from './store/loading-overlay'
import { $receiptSystemAccountId } from './store/receipt-system-account-id'
import { userInfoSource } from './store/user-info'

defineCustomClientStrategy('custom-queryParam', {
  getLocale: () => language,
  setLocale: () => {},
})

const bridge = initializeNativeBridge({
  bifrost: {
    enableDebug: import.meta.env.MODE !== 'production',
  },
  onPageVisible: () => pageVisibleOnce.done(),
})

void bridge.ready.then(() => {
  loadingBifrost.done(native)
})

void native.general.pageReady()

void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
  $receiptSystemAccountId.set(id)
})

function productIdsOf(products: Products): number[] {
  const list: Product[] = [
    products.annualOriginal,
    products.annualEarlyBird,
    products.monthlyOriginal,
    products.monthlyEarlyBird,
  ]
  if (products.monthlyNoTrial) list.push(products.monthlyNoTrial)
  return list.map((p) => p.product_id).sort((a, b) => a - b)
}

/** Tags what a page-load step throws with the step (ADR 0037). */
function inStep<T>(step: PageLoadStep, work: Promise<T>): Promise<T> {
  return work.catch((error: unknown) => {
    throw tagFailure(error, step)
  })
}

/**
 * Whether the app is older than the config requires. A malformed version
 * fails the config step.
 */
function isBelowRequiredVersion(requiredAppVersion: string): boolean {
  try {
    return compare(appVersion, requiredAppVersion, '<')
  } catch (error) {
    throw tagFailure(error, 'load_config', { cause: 'invalid_version' })
  }
}

function showDataLoadFailDialog(error: unknown, detail = '') {
  dialog.open({
    title: m.dialog_data_load_fail_title(),
    description:
      withFailure(
        m.dialog_data_load_fail_description(),
        failureOf(error, 'load'),
      ) + detail,
    button: m.ok(),
    action: () => native.general.close(),
  })
}

const env = import.meta.env.MODE as 'production' | 'staging' | 'dev'

if (import.meta.env.DEV) {
  void (async function initDev() {
    // Resolves only once MSW is intercepting (immediately when not mocking)
    // — otherwise the config fetch escapes to the real staging host, whose
    // config has no jump_points.
    await mockingReady
    let config
    try {
      config = await inStep('load_config', fetchConfig())
      $config.set(config)
    } catch (error) {
      loadingOverlay.hide()
      showDataLoadFailDialog(error)
      return
    }

    if (
      config.required_app_version &&
      isBelowRequiredVersion(config.required_app_version)
    ) {
      loadingOverlay.hide()
      dialog.open({
        title: m.dialog_update_version_title(),
        description: m.dialog_update_version_description(),
        button: m.ok(),
        action: () => native.general.close(),
      })
      return
    }

    if (platform === 'china_android') {
      $chinaAndroidTrialEligibility.set('unavailable')
    }

    inStep(
      'load_products',
      getProducts({
        cacheKeyPrefix: 'focusPass.products.v2',
        api,
        appVersion,
        platform,
        deferredUserInfo: userInfoSource.getReady(),
        env,
        shouldMock: import.meta.env.DEV,
      }),
    )
      .then((products) => {
        setProducts(products)
        void native.general.logEvent({
          event_name: 'plus_price_test_config',
          event_params: { product_ids: productIdsOf(products).join(',') },
        })
      })
      .catch((error: unknown) => {
        showDataLoadFailDialog(error)
      })
  })()
} else {
  void (async function init() {
    try {
      const config = await inStep('load_config', fetchConfig())
      $config.set(config)

      if (isBelowRequiredVersion(config.required_app_version)) {
        loadingOverlay.hide()
        dialog.open({
          title: m.dialog_update_version_title(),
          description: m.dialog_update_version_description(),
          button: m.ok(),
          action: () => native.general.close(),
        })
        return
      }

      const userInfo = await inStep('load_user_info', userInfoSource.getReady())

      await Promise.all([
        inStep(
          'load_products',
          getProducts({
            cacheKeyPrefix: 'focusPass.products.v2',
            api,
            appVersion,
            platform,
            deferredUserInfo: Promise.resolve(userInfo),
            env,
            shouldMock: false,
          }),
        ).then((products) => {
          setProducts(products)
        }),
        getChinaAndroidTrialEligibility(userInfo.user_id),
      ])
    } catch (err) {
      if (err instanceof Error && err.message === 'Connection timeout') {
        location.reload()
        return
      }
      console.error(err)
      loadingOverlay.hide()
      showDataLoadFailDialog(
        err,
        import.meta.env.MODE === 'production' ? '' : `\n${String(err)}`,
      )
      void native.general.logFatalError({
        name: 'failed_to_initialize_pass_page',
        // The payload has no params; the code leads the free-text reason.
        reason: `${failureOf(err, 'load').code} | ${String(err)}`,
      })
    }
  })()
}
